import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { environment } from '@env/environment';
import cloneDeep from 'lodash-es/cloneDeep';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { getDiff } from '../util/getObjectDiff';
import { FeatureFlagService } from './feature-flag.service';

@Injectable({
    providedIn: 'any',
})
export class FormDifferService {
    private form!: UntypedFormGroup;
    private dataSnapshot: { [key: string]: any } = {};
    private originalSnapshot: { [key: string]: any } = {};
    private useRawValues: boolean = false;

    constructor(private featureFlagService: FeatureFlagService) {}

    public registerForm(form: UntypedFormGroup, useRawValues: boolean = false) {
        this.form = form;
        this.useRawValues = useRawValues;
        this.dataSnapshot = {};
        this.originalSnapshot = {};

        if (this.useRawValues ? this.form.getRawValue() : this.form.value) {
            this.originalSnapshot = cloneDeep(this.useRawValues ? this.form.getRawValue() : this.form.value);
        }
    }

    public createDataSnapshot() {
        if (this.useRawValues ? !this.form.getRawValue() : !this.form.value) {
            throw new ReferenceError('There is no form registered to the service.');
        }

        this.dataSnapshot = cloneDeep(this.useRawValues ? this.form.getRawValue() : this.form.value);
    }

    public getDiffWithSnapshot(reverse: boolean = false): {
        [key: string]: any;
    } {
        const enableLogs = this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON_LOGS];
        if (reverse) {
            return getDiff(
                this.useRawValues ? this.form?.getRawValue() : this.form?.value,
                this.dataSnapshot,
                true,
                false,
                false,
                enableLogs,
                'FormDiffer:snapshot:reverse',
            );
        } else {
            return getDiff(
                this.dataSnapshot,
                this.useRawValues ? this.form?.getRawValue() : this.form?.value,
                true,
                false,
                false,
                enableLogs,
                'FormDiffer:snapshot',
            );
        }
    }

    public updateOriginalValueSnapshot() {
        if (this.useRawValues ? !this.form.getRawValue() : !this.form.value) {
            throw new ReferenceError('There is no form registered to the service.');
        }
        this.originalSnapshot = cloneDeep(this.useRawValues ? this.form.getRawValue() : this.form.value);
    }

    public getDiffWithOriginalValue(reverse: boolean = false): {
        [key: string]: any;
    } {
        const enableLogs = this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON_LOGS];
        if (reverse) {
            return getDiff(
                this.useRawValues ? this.form?.getRawValue() : this.form?.value,
                this.originalSnapshot,
                true,
                false,
                false,
                enableLogs,
                'FormDiffer:original:reverse',
            );
        } else {
            return getDiff(
                this.originalSnapshot,
                this.useRawValues ? this.form?.getRawValue() : this.form?.value,
                true,
                false,
                false,
                enableLogs,
                'FormDiffer:original',
            );
        }
    }

    /**
     * Returns true if the form data has changed since the snapshot was taken.
     * @returns {boolean}
     */
    public hasDataChanged(): boolean {
        if (!this.form) {
            if (!environment.production) {
                console.warn('No form registered to the form differ service.');
            }
            return false;
        }

        return Object.keys(this.getDiffWithSnapshot()).length > 0;
    }

    public getSnapshotData(): unknown {
        return this.dataSnapshot;
    }

    public getOriginalData(): unknown {
        return this.originalSnapshot;
    }
}
